/**
 * GONA — iOS / mobile product gallery video autoplay
 * Load AFTER global.js on product pages (replaces media-gallery.js).
 */
(function () {
  const DeferredMediaClass = customElements.get('deferred-media');
  if (!DeferredMediaClass) return;

  const proto = DeferredMediaClass.prototype;

  proto.isIOSDevice = function () {
    return /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  proto.prepareVideo = function (video) {
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('x5-playsinline', '');
    video.removeAttribute('autoplay');
  };

  proto.clearPlaybackWatch = function () {
    if (!this._playbackTimeout) return;
    clearTimeout(this._playbackTimeout);
    this._playbackTimeout = null;
  };

  proto.pauseVideo = function () {
    this.clearPlaybackWatch();
    const video = this.querySelector('video');
    if (video) video.pause();
  };

  proto.primeVideo = function () {
    if (this.querySelector('video')) return;

    const template = this.querySelector('template');
    if (!template) return;

    const sourceVideo = template.content.querySelector('video');
    if (!sourceVideo) return;

    const video = sourceVideo.cloneNode(true);
    this.prepareVideo(video);
    video.setAttribute('preload', 'auto');
    this.appendChild(video);
    video.load();
  };

  proto.scheduleVideoPlay = function (video, callback, userInitiated) {
    const run = () => {
      if (video.readyState >= 2) {
        callback();
        return;
      }
      video.addEventListener('loadeddata', callback, { once: true });
    };

    if (this.isIOSDevice() && !userInitiated) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setTimeout(run, 100));
      });
      return;
    }

    run();
  };

  proto.playVideoNow = function (video, userInitiated) {
    this.prepareVideo(video);

    const startPlay = () => {
      this.setAttribute('loaded', true);
      const playPromise = video.play();
      if (!playPromise) return;

      playPromise.catch(() => {
        if (!userInitiated) this.removeAttribute('loaded');
      });
    };

    if (video.readyState >= 2) {
      startPlay();
      return;
    }

    video.addEventListener('canplay', startPlay, { once: true });
    video.load();
  };

  proto.playVideo = function (video, userInitiated) {
    this.prepareVideo(video);

    const confirmPlayback = () => {
      this.clearPlaybackWatch();
      this.setAttribute('loaded', true);
    };

    const failPlayback = () => {
      this.clearPlaybackWatch();
      if (userInitiated) {
        this.setAttribute('loaded', true);
        return;
      }
      video.pause();
      this.removeAttribute('loaded');
    };

    const onPlaying = () => {
      video.removeEventListener('playing', onPlaying);
      confirmPlayback();
    };

    video.addEventListener('playing', onPlaying);

    this._playbackTimeout = setTimeout(() => {
      video.removeEventListener('playing', onPlaying);
      if (video.paused || video.readyState < 2) {
        failPlayback();
      } else {
        confirmPlayback();
      }
    }, userInitiated ? 4000 : 2500);

    const playPromise = video.play();
    if (!playPromise) return;

    playPromise.catch(() => {
      video.removeEventListener('playing', onPlaying);
      failPlayback();
    });
  };

  proto.resumeVideo = function (userInitiated) {
    const video = this.querySelector('video');
    if (!video) return;

    this.prepareVideo(video);
    if (video.ended) video.currentTime = 0;

    if (userInitiated) {
      this.playVideoNow(video, true);
      return;
    }

    const attemptPlay = () => {
      const playPromise = video.play();
      if (!playPromise) {
        this.setAttribute('loaded', true);
        return;
      }

      playPromise.then(() => {
        this.setAttribute('loaded', true);
      }).catch(() => {
        this.removeAttribute('loaded');
      });
    };

    this.scheduleVideoPlay(video, attemptPlay, false);
  };

  proto.loadContent = function (userInitiated) {
    if (userInitiated === undefined) userInitiated = true;

    if (this._loadingContent) return;
    this._loadingContent = true;
    setTimeout(() => {
      this._loadingContent = false;
    }, 300);

    try {
      if (!userInitiated) window.pauseAllMedia();

      const template = this.querySelector('template');
      const existingVideo = this.querySelector('video');

      if (existingVideo) {
        this.resumeVideo(userInitiated);
        return;
      }

      if (!template) return;

      const deferredElement = template.content.firstElementChild.cloneNode(true);
      this.appendChild(deferredElement);

      if (deferredElement.tagName === 'VIDEO') {
        deferredElement.currentTime = 0;
        deferredElement.setAttribute('preload', 'metadata');

        if (userInitiated) {
          this.playVideoNow(deferredElement, true);
        } else {
          this.scheduleVideoPlay(
            deferredElement,
            () => this.playVideo(deferredElement, false),
            false
          );
        }
        return;
      }

      this.setAttribute('loaded', true);
    } catch (error) {
      console.warn('gona-product-gallery-video loadContent failed', error);
    }
  };
})();

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };
      this.mql = window.matchMedia('(min-width: 750px)');

      this._lastActiveMediaId = null;
      this._gesturePlayLock = false;
      this.viewerSlider = this.elements.viewer?.querySelector('[id^="Slider-"]');

      if (this.elements.viewer) {
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 300));
      }

      if (this.viewerSlider) {
        this.viewerSlider.addEventListener('touchstart', this.onViewerTouchStart.bind(this), { passive: true });
        this.viewerSlider.addEventListener('touchend', this.onViewerGestureEnd.bind(this), { passive: true });
        this.viewerSlider.addEventListener('pointerup', this.onViewerPointerUp.bind(this), { passive: true });
        if ('onscrollend' in window) {
          this.viewerSlider.addEventListener('scrollend', this.onViewerScrollEnd.bind(this), { passive: true });
        }
      }

      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    getCurrentSlide() {
      if (!this.viewerSlider) return null;

      const slides = Array.from(this.viewerSlider.querySelectorAll('[id^="Slide-"]'))
        .filter((slide) => slide.clientWidth > 0);

      if (!slides.length) return null;

      const viewportCenter = this.viewerSlider.scrollLeft + this.viewerSlider.clientWidth / 2;
      let closest = slides[0];
      let minDistance = Infinity;

      slides.forEach((slide) => {
        const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
        const distance = Math.abs(slideCenter - viewportCenter);
        if (distance < minDistance) {
          minDistance = distance;
          closest = slide;
        }
      });

      return closest;
    }

    activateSlide(slide, userInitiated) {
      if (userInitiated === undefined) userInitiated = false;
      if (!slide?.dataset?.mediaId || !this.elements.viewer) return null;

      try {
        const mediaId = slide.dataset.mediaId;
        const isSameSlide = this._lastActiveMediaId === mediaId;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        slide.classList.add('is-active');
        this._lastActiveMediaId = mediaId;

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          this.setActiveThumbnail(thumbnail);
        }

        if (!isSameSlide || userInitiated) {
          this.playActiveMedia(slide, userInitiated);
        }

        return mediaId;
      } catch (error) {
        console.warn('gona-product-gallery-video activateSlide failed', error);
        return null;
      }
    }

    onSlideChanged(event) {
      this.activateSlide(event.detail.currentElement, false);
    }

    primeGalleryVideos() {
      if (!this.elements.viewer) return;

      this.elements.viewer.querySelectorAll('.deferred-media').forEach((deferredMedia) => {
        if (typeof deferredMedia.primeVideo === 'function') deferredMedia.primeVideo();
      });
    }

    onViewerTouchStart() {
      this.primeGalleryVideos();
    }

    playSlideAfterGesture() {
      const slide = this.getCurrentSlide();
      if (slide) this.activateSlide(slide, true);
    }

    onViewerGestureEnd() {
      if (this._gesturePlayLock) return;

      this._gesturePlayLock = true;
      setTimeout(() => {
        this._gesturePlayLock = false;
      }, 250);

      this.playSlideAfterGesture();
      requestAnimationFrame(() => this.playSlideAfterGesture());
      setTimeout(() => this.playSlideAfterGesture(), 120);
    }

    onViewerScrollEnd() {
      const slide = this.getCurrentSlide();
      if (!slide) return;

      const mediaId = slide.dataset.mediaId;
      if (this._lastActiveMediaId === mediaId) {
        this.playActiveMedia(slide, false);
      }
    }

    onViewerPointerUp(event) {
      if (event.pointerType === 'touch') return;
      const slide = this.getCurrentSlide();
      if (slide) this.activateSlide(slide, true);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia, true);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    pauseInactiveMedia(activeItem) {
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        if (element === activeItem) return;

        const deferredMedia = element.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.pauseVideo === 'function') {
          deferredMedia.pauseVideo();
        }

        element.querySelectorAll('.js-youtube').forEach((iframe) => {
          iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        });
        element.querySelectorAll('.js-vimeo').forEach((iframe) => {
          iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
        });
      });
    }

    playActiveMedia(activeItem, userInitiated) {
      if (userInitiated === undefined) userInitiated = false;
      this.pauseInactiveMedia(activeItem);

      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (!deferredMedia) return;

      if (deferredMedia.querySelector('video')) {
        deferredMedia.resumeVideo(userInitiated);
        return;
      }

      deferredMedia.loadContent(userInitiated);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
